import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { toast } from 'sonner';

/**
 * Build a full, absolute URL for HTML5 Audio playback.
 * ONLY returns URLs that are actual audio files (direct uploads, GridFS).
 * DSP page/embed URLs (spotify.com, soundcloud.com, etc.) are NOT audio
 * files and must never be fed to <audio> — they cause MEDIA_ERR_SRC_NOT_SUPPORTED.
 */
const buildAudioUrl = (track) => {
  if (track.audio_unavailable) return null;

  // If backend computed playable_type === "audio", trust that URL
  if (track.playable_type === 'audio' && track.playable_url) {
    const url = track.playable_url;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  }

  // Only consider URLs that point to actual audio files
  const audioUrls = [
    track.audio_url,
    track.file_url,
  ].filter(Boolean);

  for (const url of audioUrls) {
    if (!url) continue;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  }

  return null;
};

/**
 * Get the best DSP / streaming link for external playback.
 */
const getDspFallbackUrl = (track) => {
  return track.dsp_url || track.spotify_url || track.apple_music_url
      || track.external_url || track.embed_url || null;
};

/**
 * Detect iOS (all browsers use WebKit on iOS, so all need the same workarounds).
 */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const audioRef = useRef(null);
  const playPromiseRef = useRef(null);
  const currentTrackRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Keep the ref in sync with state
  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  const showAudioFallback = (track) => {
    const dspFallback = getDspFallbackUrl(track);
    if (dspFallback) {
      toast.info(`Opening "${track.title}" externally`, { description: 'Direct playback unavailable' });
      window.open(dspFallback, '_blank');
    } else {
      toast.error(`"${track.title || 'Track'}" is temporarily unavailable`, {
        description: 'The audio file needs to be re-uploaded'
      });
    }
  };

  /**
   * Create the audio element with iOS-compatible attributes.
   * Uses a DOM <audio> element with crossOrigin for CORS support.
   */
  useEffect(() => {
    if (!audioRef.current) {
      const audio = document.createElement('audio');
      audio.setAttribute('playsinline', '');
      audio.setAttribute('webkit-playsinline', '');
      audio.crossOrigin = 'anonymous';
      audio.preload = 'auto';
      audio.volume = volume;
      // Keep it in the DOM but hidden (iOS works better with DOM elements)
      audio.style.display = 'none';
      document.body.appendChild(audio);
      audioRef.current = audio;
      
      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });
      
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      audio.addEventListener('ended', () => {
        playNext();
      });
      
      audio.addEventListener('error', (e) => {
        const mediaError = audio?.error;
        // Ignore errors when src is empty or a data URI (unlock phase)
        if (!audio.src || audio.src === '' || audio.src.startsWith('data:')) return;
        
        console.error('[Player] Audio error:', mediaError?.code, mediaError?.message);
        setIsPlaying(false);
        // MediaError code 4 = MEDIA_ERR_SRC_NOT_SUPPORTED (file missing or format issue)
        if (mediaError?.code === 4 && currentTrackRef.current) {
          const t = currentTrackRef.current;
          // On iOS, retry without crossOrigin (some servers don't support CORS preflight)
          if (isIOS() && audio.crossOrigin) {
            console.log('[Player] iOS error 4 - retrying without CORS...');
            audio.crossOrigin = null;
            audio.load();
            const retryPlay = audio.play();
            if (retryPlay) {
              retryPlay.then(() => {
                setIsPlaying(true);
              }).catch(() => {
                // Retry also failed, show fallback
                showAudioFallback(t);
              });
            }
            return;
          }
          showAudioFallback(t);
        }
      });
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        if (audioRef.current.parentNode) {
          audioRef.current.parentNode.removeChild(audioRef.current);
        }
      }
    };
  }, []);

  /**
   * iOS audio unlock: the very first play() must originate from a
   * synchronous user-gesture call-stack. We prime the audio element
   * on the first tap/click anywhere on the page using a minimal approach.
   */
  useEffect(() => {
    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      const audio = audioRef.current;
      if (!audio) return;
      
      // Simple unlock: just call play() on the existing element
      // This is enough to "unlock" the audio context on iOS
      const origSrc = audio.src;
      const hadSrc = !!origSrc && origSrc !== window.location.href;
      
      // If no source set, use a minimal silent WAV (more compatible than mp3 data URI)
      if (!hadSrc) {
        // Minimal valid WAV: 44 bytes header + 2 bytes of silence
        audio.src = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQIAAAAAAA==';
      }
      
      const p = audio.play();
      if (p) {
        p.then(() => {
          audio.pause();
          audio.currentTime = 0;
          if (!hadSrc) {
            audio.removeAttribute('src');
            audio.load(); // Reset the element
          }
          audioUnlockedRef.current = true;
          console.log('[Player] Audio element unlocked for iOS');
        }).catch(() => {
          // Silent fail - will unlock on actual play
          if (!hadSrc) {
            audio.removeAttribute('src');
          }
        });
      }
      // Remove listeners after first attempt
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('touchend', unlockAudio, true);
      document.removeEventListener('click', unlockAudio, true);
    };

    // Listen for first user interaction to unlock audio
    document.addEventListener('touchstart', unlockAudio, true);
    document.addEventListener('touchend', unlockAudio, true);
    document.addEventListener('click', unlockAudio, true);

    return () => {
      document.removeEventListener('touchstart', unlockAudio, true);
      document.removeEventListener('touchend', unlockAudio, true);
      document.removeEventListener('click', unlockAudio, true);
    };
  }, []);

  const playTrack = async (track, trackList = []) => {
    if (!track) return;
    
    // Check if backend flagged this track as having broken audio
    if (track.audio_unavailable) {
      const dspFallback = getDspFallbackUrl(track);
      if (dspFallback) {
        toast.info(`Opening "${track.title}" externally`, {
          description: 'Original audio file is no longer available'
        });
        window.open(dspFallback, '_blank');
      } else {
        toast.error(`"${track.title}" is temporarily unavailable`, {
          description: 'The audio file for this track needs to be re-uploaded'
        });
      }
      return;
    }
    
    setCurrentTrack(track);
    
    if (trackList.length > 0) {
      const currentIndex = trackList.findIndex(t => t.track_id === track.track_id);
      const newQueue = trackList.slice(currentIndex + 1);
      setQueue(newQueue);
    }
    
    if (audioRef.current) {
      // Build audio URL — ONLY returns real audio file URLs, never DSP page links
      const audioUrl = buildAudioUrl(track);
      
      // No local audio file — redirect to streaming platform
      if (!audioUrl) {
        const dspFallback = getDspFallbackUrl(track);
        if (dspFallback) {
          let platform = 'streaming service';
          if (dspFallback.includes('apple.com')) platform = 'Apple Music';
          else if (dspFallback.includes('spotify.com')) platform = 'Spotify';
          else if (dspFallback.includes('soundcloud.com')) platform = 'SoundCloud';
          else if (dspFallback.includes('audiomack.com')) platform = 'Audiomack';
          else if (dspFallback.includes('youtube.com') || dspFallback.includes('youtu.be')) platform = 'YouTube';
          else if (dspFallback.includes('tidal.com')) platform = 'Tidal';
          else if (dspFallback.includes('deezer.com')) platform = 'Deezer';
          
          toast.info(`Opening "${track.title}" on ${platform}`, {
            description: 'Listen on the streaming platform',
            action: {
              label: 'Open',
              onClick: () => window.open(dspFallback, '_blank')
            }
          });
          window.open(dspFallback, '_blank');
        } else {
          toast.error('Audio unavailable for this track');
        }
        setIsPlaying(false);
        return;
      }
      
      // Has actual audio file — play via HTML5 Audio
      console.log('[Player] Playing track:', track.title, '| URL:', audioUrl);
      
      // Safely abort any pending play before starting new one
      if (playPromiseRef.current) {
        try {
          audioRef.current.pause();
        } catch {}
      }
      
      // Set source and explicitly call load() — critical for iOS Safari.
      audioRef.current.src = audioUrl;
      audioRef.current.load();
      
      try {
        playPromiseRef.current = audioRef.current.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
        setIsPlaying(true);
      } catch (e) {
        playPromiseRef.current = null;
        if (e.name === 'AbortError') {
          console.debug('[Player] Playback interrupted - new track selected');
          return;
        }
        console.error('Playback error:', e?.name, e?.message);
        
        // iOS Safari retry: wait for canplay event then try again
        if (e.name === 'NotSupportedError' || e.name === 'NotAllowedError') {
          console.log('[Player] Retrying playback after canplaythrough event...');
          try {
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                audioRef.current.removeEventListener('error', onError);
                resolve();
              };
              const onError = () => {
                audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                audioRef.current.removeEventListener('error', onError);
                reject(new Error('Audio load failed'));
              };
              audioRef.current.addEventListener('canplaythrough', onCanPlay, { once: true });
              audioRef.current.addEventListener('error', onError, { once: true });
              setTimeout(() => {
                audioRef.current.removeEventListener('canplaythrough', onCanPlay);
                audioRef.current.removeEventListener('error', onError);
                reject(new Error('Timeout'));
              }, 5000);
            });
            playPromiseRef.current = audioRef.current.play();
            await playPromiseRef.current;
            playPromiseRef.current = null;
            setIsPlaying(true);
            console.log('[Player] Retry succeeded');
          } catch (retryErr) {
            playPromiseRef.current = null;
            console.error('[Player] Retry failed:', retryErr?.name, retryErr?.message);
            setIsPlaying(false);
            // Final fallback: open externally if DSP available
            const dspFallback = getDspFallbackUrl(track);
            if (dspFallback) {
              toast.info(`Opening "${track.title}" externally`, {
                description: 'Direct playback unavailable on this device'
              });
              window.open(dspFallback, '_blank');
            } else {
              toast.error(`"${track.title}" is temporarily unavailable`, {
                description: 'The audio file may need to be re-uploaded'
              });
            }
          }
        } else {
          setIsPlaying(false);
          // Try DSP fallback for any other error too
          const dspFallback = getDspFallbackUrl(track);
          if (dspFallback) {
            toast.info(`Opening "${track.title}" externally`, {
              description: 'Direct playback unavailable'
            });
            window.open(dspFallback, '_blank');
          } else {
            toast.error('Unable to play this track');
          }
        }
        return;
      }
      
      // Record play AFTER starting playback (fire-and-forget, non-blocking)
      fetch(`${API_BASE_URL}/api/tracks/${track.track_id}/play`, {
        method: 'POST'
      }).catch(e => console.error('Failed to record play:', e));
    }
  };

  const playVideo = async (video) => {
    // Record the play only - navigation is handled by the caller
    if (!video?.video_id) return;
    fetch(`${API_BASE_URL}/api/videos/${video.video_id}/play`, {
      method: 'POST'
    }).catch(e => console.error('Failed to record video play:', e));
  };

  const togglePlay = async () => {
    if (!audioRef.current || !currentTrack) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      playPromiseRef.current = null;
      setIsPlaying(false);
    } else {
      try {
        // If audio has no src or it was cleared, reload it
        if (!audioRef.current.src || audioRef.current.src === window.location.href) {
          const audioUrl = buildAudioUrl(currentTrack);
          if (audioUrl) {
            audioRef.current.src = audioUrl;
            audioRef.current.load();
          }
        }
        playPromiseRef.current = audioRef.current.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
        setIsPlaying(true);
      } catch (e) {
        playPromiseRef.current = null;
        if (e.name !== 'AbortError') {
          console.warn('Playback prevented:', e?.name, e?.message);
        }
        setIsPlaying(false);
      }
    }
  };

  const playNext = () => {
    if (queue.length > 0) {
      const [next, ...rest] = queue;
      setQueue(rest);
      playTrack(next);
    } else {
      setIsPlaying(false);
    }
  };

  const playPrevious = () => {
    if (audioRef.current) {
      if (currentTime > 3) {
        audioRef.current.currentTime = 0;
      }
    }
  };

  const seek = (time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const changeVolume = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const addToQueue = (track) => {
    setQueue(prev => [...prev, track]);
  };

  const clearQueue = () => {
    setQueue([]);
  };

  const shuffleQueue = () => {
    setQueue(prev => {
      const shuffled = [...prev];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    });
  };

  const playPlaylist = (tracks, startIndex = 0) => {
    if (!tracks || tracks.length === 0) return;
    const start = tracks[startIndex];
    const rest = [...tracks.slice(startIndex + 1), ...tracks.slice(0, startIndex)];
    setQueue(rest);
    playTrack(start);
  };

  return (
    <PlayerContext.Provider value={{
      currentTrack,
      isPlaying,
      queue,
      currentTime,
      duration,
      volume,
      playTrack,
      playVideo,
      togglePlay,
      playNext,
      playPrevious,
      seek,
      changeVolume,
      addToQueue,
      clearQueue,
      shuffleQueue,
      playPlaylist
    }}>
      {children}
    </PlayerContext.Provider>
  );
};
